import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { DESKTOP_QUERY } from 'src/app/shared/utils/breakpoints';

export const HIDE_SHELL_TOOLBAR_ON_MOBILE = 'hideShellToolbarOnMobile';
export const DOCUMENT_SCROLL_ON_MOBILE = 'documentScrollOnMobile';
export const SHELL_BARE_CLASS = 'shell-bare';

/** Coordinates mobile document scrolling and toolbar ownership from route data. */
@Injectable({ providedIn: 'root' })
export class ShellChromeService {
  private readonly router = inject(Router);
  private readonly breakpoints = inject(BreakpointObserver);

  private readonly routeData = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => leafData(this.router.routerState.snapshot.root)),
      startWith(leafData(this.router.routerState.snapshot.root))
    ),
    { initialValue: leafData(this.router.routerState.snapshot.root) }
  );

  private readonly desktop = toSignal(this.breakpoints.observe([DESKTOP_QUERY]).pipe(map((result) => result.matches)), {
    initialValue: this.breakpoints.isMatched(DESKTOP_QUERY),
  });

  /** True while the active route owns the page chrome and the document scroll. */
  readonly bare = computed(() => this.routeData()[HIDE_SHELL_TOOLBAR_ON_MOBILE] === true && !this.desktop());
  readonly documentScroll = computed(
    () => this.bare() || (this.routeData()[DOCUMENT_SCROLL_ON_MOBILE] === true && !this.desktop())
  );

  constructor() {
    const root = inject(DOCUMENT).documentElement;
    effect((onCleanup) => {
      root.classList.toggle(SHELL_BARE_CLASS, this.documentScroll());
      onCleanup(() => root.classList.remove(SHELL_BARE_CLASS));
    });
  }
}

function leafData(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot['data'] {
  let leaf = root;
  while (leaf.firstChild) leaf = leaf.firstChild;
  return leaf.data;
}
